import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LockIcon from "@mui/icons-material/Lock";
import CloseIcon from "@mui/icons-material/Close";
import {
    Box,
    Button,
    Divider,
    IconButton,
    Modal,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { colors } from "../../../../ui/colors";
import { SnackbarAlert } from "../../../../common/SnackbarAlert";

interface PublishSiteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onPublish: (slug: string) => void | Promise<void>;
    businessUrl?: string;
    slug: string;
    onChangeSlug: (slug: string) => void;
}

export function PublishSiteModal({
    isOpen,
    onClose,
    onPublish,
    businessUrl = "app.flownect.com",
    slug,
    onChangeSlug,
}: PublishSiteModalProps) {
    const queryClient = useQueryClient();

    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isCopied, setIsCopied] = useState(false);

    // Snackbar
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState<
        "success" | "info" | "warning" | "error"
    >("info");
    const [snackbarMessage, setSnackbarMessage] = useState("");

    const fullUrl = useMemo(
        () => `https://${businessUrl}/marketing/${slug}`,
        [businessUrl, slug],
    );

    const validateSlug = (value: string): boolean => {
        if (!value) return setError("Site name is required"), false;
        if (!/^[a-z0-9-]+$/.test(value))
            return setError("Only lowercase letters, numbers, and hyphens are allowed"), false;
        if (value.length < 3) return setError("Site name must be at least 3 characters"), false;
        if (value.length > 50) return setError("Site name cannot exceed 50 characters"), false;
        setError("");
        return true;
    };

    const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toLowerCase();
        onChangeSlug(value);
        if (value) validateSlug(value);
    };

    const handlePublish = async () => {
        if (!validateSlug(slug)) return;

        setIsLoading(true);
        try {
            await onPublish(slug);

            // optional: revalidate if you have a "my sites" query
            // await queryClient.invalidateQueries({ queryKey: ["pageBuilder", "sites"] });

            setSnackbarSeverity("success");
            setSnackbarMessage("Your site has been published successfully.");
            setSnackbarOpen(true);

            // optional: close modal after success
            // onClose();
        } catch (e) {
            setSnackbarSeverity("error");
            setSnackbarMessage("Something went wrong. Please try again.");
            setSnackbarOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && !error && slug) handlePublish();
        if (e.key === "Escape") onClose();
    };

    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(fullUrl);
            setIsCopied(true);

            setSnackbarSeverity("success");
            setSnackbarMessage("URL copied to clipboard");
            setSnackbarOpen(true);

            setTimeout(() => setIsCopied(false), 1200);
        } catch {
            setSnackbarSeverity("error");
            setSnackbarMessage("Could not copy URL");
            setSnackbarOpen(true);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={modalSx} onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                        <Box>
                            <Typography sx={titleSx}>Almost there!</Typography>
                            <Typography sx={subtitleSx}>
                                Set the name that will appear in your website URL. This will be the address visitors use to
                                find your site.
                            </Typography>
                        </Box>

                        <IconButton onClick={onClose} sx={closeIconBtnSx} aria-label="Close">
                            <CloseIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    {/* URL Preview */}
                    <Box sx={urlPreviewSx}>
                        <Typography component="code" sx={codeSx}>
                            {fullUrl}
                        </Typography>

                        <IconButton onClick={handleCopyUrl} sx={copyBtnSx} aria-label="Copy url">
                            {isCopied ? (
                                <motion.span initial={{ scale: 0.9 }} animate={{ scale: 1 }} transition={{ duration: 0.15 }}>
                                    ✓
                                </motion.span>
                            ) : (
                                <AssignmentIcon sx={{ fontSize: 18 }} />
                            )}
                        </IconButton>
                    </Box>

                    {/* Input Group */}
                    <Box sx={{ mt: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography sx={labelSx}>Website name</Typography>
                        </Stack>

                        {/* Prefix + input */}
                        <Box sx={inputContainerSx(error)}>
                            <Box sx={prefixSx}>https://{businessUrl}/marketing/</Box>

                            {/* Input wrapper to embed lock inside input area WITHOUT absolute positioning */}
                            <Box sx={{ display: "flex", alignItems: "center", flex: 1, px: 2 }}>
                                <input
                                    id="slug-input"
                                    type="text"
                                    value={slug}
                                    onChange={handleSlugChange}
                                    onKeyDown={handleKeyDown}
                                    placeholder="my-site"
                                    disabled
                                    style={inputInlineStyle}
                                    autoFocus
                                />

                                <Tooltip
                                    title="If you want a custom URL, contact us"
                                    placement="top"
                                    arrow
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: colors.gray[300] }}>
                                        <LockIcon sx={{ fontSize: 18 }} />
                                    </Box>
                                </Tooltip>

                            </Box>
                        </Box>

                        {error ? (
                            <Typography sx={errorSx}>{error}</Typography>
                        ) : (
                            <Typography sx={helperSx}>
                                This URL cannot be modified. Contact us for custom URLs.
                            </Typography>
                        )}
                    </Box>

                    {/* Actions */}
                    <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 3 }}>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            disabled={isLoading}
                            sx={secondaryBtnSx}
                        >
                            Not now
                        </Button>

                        <Button
                            variant="contained"
                            onClick={handlePublish}
                            disabled={!slug || !!error || isLoading}
                            sx={primaryBtnSx}
                        >
                            {isLoading ? "Publishing..." : "Publish"}
                        </Button>
                    </Stack>
                </Box>
            </Modal>

            <SnackbarAlert
                openState={snackbarOpen}
                setOpenState={setSnackbarOpen}
                severity={snackbarSeverity}
                message={snackbarMessage}
                duration={2500}
                position="bottom-center"
                scope="global"
            />
        </>
    );
}

/** ================== MUI SX ================== */

const modalSx = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "min(620px, 92vw)",
    bgcolor: "var(--fx-surface-base, #FFFFFF)",
    borderRadius: "16px",
    boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
    p: 4,
    outline: "none",
};

const titleSx = {
    fontSize: 24,
    fontWeight: 700,
    color: colors.gray[500],
    lineHeight: 1.2,
};

const subtitleSx = {
    mt: 0.75,
    fontSize: 14,
    color: colors.gray[300],
    lineHeight: 1.6,
    maxWidth: 520,
};

const closeIconBtnSx = {
    color: colors.gray[300],
    borderRadius: 2,
};

const urlPreviewSx = {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    p: 2,
    borderRadius: 2,
    border: "1px solid var(--fx-surface-border, #E2E8F0)",
    backgroundColor: colors.lightGray?.soft ?? "var(--fx-surface-muted, #F1F5F9)",
};

const codeSx = {
    fontSize: 13,
    color: colors.orange.base,
    fontFamily: '"Courier New", monospace',
    wordBreak: "break-all" as const,
};

const copyBtnSx = {
    ml: "auto",
    color: colors.gray[300],
    borderRadius: 2,
};

const labelSx = {
    fontSize: 14,
    fontWeight: 600,
    color: colors.gray[400],
};

const inputContainerSx = (error: string) => ({
    display: "flex",
    alignItems: "stretch",
    borderRadius: 2,
    border: `2px solid ${error ? colors.danger.base : "#D1D5DB"}`,
    overflow: "hidden",
    transition: "all 0.2s",
    boxShadow: error ? `0 0 0 3px ${colors.danger[50]}` : "none",
});

const prefixSx = {
    display: "flex",
    alignItems: "center",
    px: 2,
    py: 1.5,
    fontSize: 13,
    color: colors.gray[300],
    backgroundColor: colors.lightGray?.base ?? "var(--fx-surface-sunken, #F8FAFC)",
    borderRight: "1px solid var(--fx-surface-border, #E2E8F0)",
    fontFamily: '"Courier New", monospace',
    whiteSpace: "nowrap" as const,
};

const inputInlineStyle: React.CSSProperties = {
    flex: 1,
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: 14,
    fontWeight: 500,
    color: colors.gray[500],
    padding: "12px 0",
    cursor: "not-allowed",
};

const helperSx = {
    mt: 1,
    fontSize: 12,
    color: colors.gray[300],
};

const errorSx = {
    mt: 1,
    fontSize: 12,
    color: colors.danger.base,
};

const primaryBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 2,
    backgroundColor: colors.orange.base,
    "&:hover": { backgroundColor: colors.orange.base },
};

const secondaryBtnSx = {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 2,
    color: colors.gray[400],
    borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
};
